import React from 'react';
import { useError } from '../contexts/ErrorContext';
import { extractErrorMessage } from '../utils/errorHandling';
import { storeOAuthState } from '../utils/oauth';
import { useOAuthLoading } from '../hooks/useOAuthLoading';
import { authApi } from '../api/auth';

// Validate OAuth configuration at module level
const OAUTH_CONFIG = {
  google: process.env.REACT_APP_GOOGLE_CLIENT_ID,
  apple: process.env.REACT_APP_APPLE_CLIENT_ID,
  github: process.env.REACT_APP_GITHUB_CLIENT_ID,
};

// Log warnings for missing providers (allows partial OAuth deployment)
Object.entries(OAUTH_CONFIG).forEach(([provider, clientId]) => {
  if (!clientId) {
    console.warn(`OAuth provider "${provider}" is not configured (missing REACT_APP_${provider.toUpperCase()}_CLIENT_ID)`);
  }
});

interface SocialLoginProps {
  mode?: 'login' | 'register';
}

const SocialLogin: React.FC<SocialLoginProps> = ({ mode = 'login' }) => {
  const { addError } = useError();
  const { setLoading, isLoading } = useOAuthLoading();

  // Google and Apple OAuth now use standard redirect flow (removed SDKs)

  const handleGoogleLogin = async () => {
    if (!OAUTH_CONFIG.google) {
      addError('Google Sign In is not configured. Please contact support.');
      return;
    }

    setLoading('google', true);
    try {
      // Fetch CSRF state token from backend
      const response = await authApi.googleInitiate();
      const state = response.data.state;

      // Store state for client-side verification
      storeOAuthState('google', state);

      const clientId = OAUTH_CONFIG.google;
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const scope = 'openid email profile';

      // Redirect to Google OAuth
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&state=${state}`;
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to initiate Google login. Please try again.');
      addError(apiError.message);
      setLoading('google', false);
    }
  };

  const handleAppleLogin = async () => {
    if (!OAUTH_CONFIG.apple) {
      addError('Apple Sign In is not configured. Please contact support.');
      return;
    }

    setLoading('apple', true);
    try {
      // Fetch CSRF state token from backend
      const response = await authApi.appleInitiate();
      const state = response.data.state;

      // Store state for client-side verification
      storeOAuthState('apple', state);

      const clientId = OAUTH_CONFIG.apple;
      const redirectUri = `${window.location.origin}/auth/apple/callback`;
      const scope = 'name email';

      // Redirect to Apple OAuth
      // Note: Apple uses response_mode=form_post by default, but we use query for web compatibility
      window.location.href = `https://appleid.apple.com/auth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&response_mode=query&scope=${encodeURIComponent(scope)}&state=${state}`;
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to initiate Apple login. Please try again.');
      addError(apiError.message);
      setLoading('apple', false);
    }
  };

  const handleGitHubLogin = async () => {
    if (!OAUTH_CONFIG.github) {
      addError('GitHub Sign In is not configured. Please contact support.');
      return;
    }

    setLoading('github', true);
    try {
      // Fetch CSRF state token from backend using API client
      const response = await authApi.githubInitiate();
      const state = response.data.state;

      // Store state for client-side verification
      storeOAuthState('github', state);

      const clientId = OAUTH_CONFIG.github;
      const redirectUri = `${window.location.origin}/auth/github/callback`;
      const scope = 'user:email';

      // Redirect to GitHub OAuth with backend-generated state
      window.location.href = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${state}`;
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to initiate GitHub login. Please try again.');
      addError(apiError.message);
      setLoading('github', false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">
            Or {mode === 'login' ? 'sign in' : 'create account'} with
          </span>
        </div>
      </div>

      {/* Informational message based on mode */}
      {mode === 'register' && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
          <p className="font-medium mb-1">Quick and secure sign up</p>
          <p className="text-blue-700">Creating an account with social login is instant and secure. Your account will be ready immediately!</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {/* Google Sign In */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={isLoading('google')}
          className="w-full inline-flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {isLoading('google') ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700"></div>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </>
          )}
        </button>

        {/* Apple Sign In */}
        <button
          type="button"
          onClick={handleAppleLogin}
          disabled={isLoading('apple')}
          className="w-full inline-flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm bg-black text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {isLoading('apple') ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Continue with Apple
            </>
          )}
        </button>

        {/* GitHub Sign In */}
        <button
          type="button"
          onClick={handleGitHubLogin}
          disabled={isLoading('github')}
          className="w-full inline-flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {isLoading('github') ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z"
                />
              </svg>
              Continue with GitHub
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SocialLogin;
